import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { NS, ORG_SETTINGS_KEYS } from '../../../../i18n/constants/keys';
import RichTextEditor from '../../../../ui/RichTextEditor';
import {
  ICandidateStatusTemplate,
  getCandidateStatusTemplates,
  resetCandidateStatusTemplate,
  updateCandidateStatusTemplate,
} from '../../../../utils/api_service/organization';
import LoadingSpinner from '../../../job_openings/create/ui/LoaderSpinner';

/**
 * Lets an organization replace the wording of the candidate status emails.
 *
 * Only the Unsuccessful email is editable here for now; the API already serves
 * Waitlisted and Shortlisted, so adding them is a matter of extending
 * EDITABLE_TEMPLATES below once the copy for them has been agreed.
 *
 * Where a tenant has not customised a template the API returns the platform
 * default, so the editor always opens on something sensible and "Reset" simply
 * deletes the override.
 */
const EDITABLE_TEMPLATES: ICandidateStatusTemplate['templateKey'][] = ['unsuccessful'];

/** The editor round-trips its own JSON; HTML-only templates need wrapping. */
const asEditorDocument = (template: ICandidateStatusTemplate): string => {
  if (template.templateJson) return template.templateJson;
  return JSON.stringify({
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [{ type: 'text', text: stripHtml(template.templateHtml) }],
      },
    ],
  });
};

/** The built-in defaults are raw HTML strings, not editor documents. */
const stripHtml = (html: string): string =>
  (html || '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const EmailSettings = () => {
  const { t } = useTranslation(NS.organizationSettings);
  const [templates, setTemplates] = useState<ICandidateStatusTemplate[]>([]);
  const [variables, setVariables] = useState<{ name: string; variable: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await getCandidateStatusTemplates();
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await response.json();
      setTemplates(data.templates ?? []);
      setVariables(data.variables ?? []);
    } catch {
      toast.error(t(ORG_SETTINGS_KEYS.emailSettings.loadFailed), {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (
    templateKey: string,
    templateHtml: string,
    templateJson: string,
  ) => {
    setSaving(true);
    try {
      const response = await updateCandidateStatusTemplate(templateKey, {
        templateHtml,
        templateJson,
      });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      toast.success(t(ORG_SETTINGS_KEYS.emailSettings.saved), {
        position: 'bottom-center',
      });
      await load();
    } catch {
      toast.error(t(ORG_SETTINGS_KEYS.emailSettings.saveFailed), {
        position: 'bottom-center',
      });
    } finally {
      setSaving(false);
    }
  };

  const reset = async (templateKey: string) => {
    setSaving(true);
    try {
      const response = await resetCandidateStatusTemplate(templateKey);
      if (!response.ok) throw new Error(`Status ${response.status}`);
      toast.success(t(ORG_SETTINGS_KEYS.emailSettings.reset), {
        position: 'bottom-center',
      });
      await load();
    } catch {
      toast.error(t(ORG_SETTINGS_KEYS.emailSettings.resetFailed), {
        position: 'bottom-center',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-[60vh]'>
        <LoadingSpinner label='' />
      </div>
    );
  }

  const editable = templates.filter((tpl) =>
    EDITABLE_TEMPLATES.includes(tpl.templateKey));

  return (
    <div className='w-full flex flex-col gap-6 p-5'>
      { editable.map((template) => (
        <section
            key={template.templateKey}
            className='rounded-xl border border-gray-200 bg-white p-5'
        >
          <div className='mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
            <div>
              <h3 className='text-lg font-semibold text-slate-900'>
                { t(ORG_SETTINGS_KEYS.emailSettings.unsuccessfulTitle) }
              </h3>
              <p className='mt-1 text-sm text-slate-600'>
                { t(ORG_SETTINGS_KEYS.emailSettings.unsuccessfulDescription) }
              </p>
              <p className='mt-1 text-xs text-slate-500'>
                { template.isCustomised
                  ? t(ORG_SETTINGS_KEYS.emailSettings.customised)
                  : t(ORG_SETTINGS_KEYS.emailSettings.usingDefault) }
              </p>
            </div>
            { template.isCustomised && (
              <button
                  type='button'
                  disabled={saving}
                  onClick={() => reset(template.templateKey)}
                  className='self-start rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 disabled:opacity-50'
              >
                { t(ORG_SETTINGS_KEYS.emailSettings.resetButton) }
              </button>
            ) }
          </div>

          <RichTextEditor
              jsonStringTemplate={asEditorDocument(template)}
              validVariables={variables}
              buttonText={t(ORG_SETTINGS_KEYS.emailSettings.saveButton)}
              saveTemplate={(html, json) => save(template.templateKey, html, json)}
          />
        </section>
      )) }

      { editable.length === 0 && (
        <p className='text-sm text-slate-500'>
          { t(ORG_SETTINGS_KEYS.emailSettings.loadFailed) }
        </p>
      ) }
    </div>
  );
};
